import React, { useState, useEffect } from "react";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { useTranslation } from "react-i18next";
import {
  useConfig,
  usePayment,
  usePaymentMethods,
  useTicket,
} from "../../../store/Store";
import { saveStripePaymentMethod } from "../utils/paymentMethodsHelpers";
import Currency from "../../general/Currency";
import SurchargeConfirmationModal from "../../surcharge/modal";
import { useStripePayment } from "../hooks/useStripePayment";
import { getBalanceDue } from "../../general/AppHelpers";

const StripePaymentForm = ({ addPaymentMethod = false }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { t } = useTranslation();
  const { success: paymentSuccess, isProcessing } = usePayment();
  const { selectedPaymentMethod, saveStripePaymentMethodLoading, error } = usePaymentMethods();
  const { data: ticketData } = useTicket();
  const [cardEntered, setCardEntered] = useState(false);
  const { accessToken } = useConfig();

  const {
    handleSubmit,
    handleCancelSurchargeModal,
    proceedToPayment,
    showSurchargeConfirmationModal,
    isProcessingNewCard,
  } = useStripePayment({ stripe, elements, selectedPaymentMethod, t });

  useEffect(() => {
    if (!stripe || !elements) return;
    const card = elements.getElement(CardElement);
    card.on("change", (e) => setCardEntered(e.complete));
  }, [stripe, elements]);

  const handleAddPaymentMethod = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    await saveStripePaymentMethod({ stripe, elements, accessToken });
  };

  const paymentAmount = getBalanceDue(ticketData);

  if (paymentSuccess) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-md p-4 mt-4">
        <h3 className="text-sm font-medium text-green-800">
          {t("payment.success")}
        </h3>
        <p className="text-sm text-green-700 mt-2">
          {t("payment.success_confirmation")}
        </p>
      </div>
    );
  }

  return (
    <div className="mt-4 border border-gray-200 rounded-md p-4 bg-gray-50">
      {addPaymentMethod ? (
        <form onSubmit={handleAddPaymentMethod}>
          <label className="block text-gray-700 text-sm font-bold mb-2">
            {t("payment_methods.card_details")}
          </label>
          <div className="border rounded-md p-3 bg-white">
            <CardElement options={{ hidePostalCode: true }} />
          </div>

          {error && <p className="text-red-600 text-sm mt-2">{error}</p>}

          <button
            type="submit"
            disabled={!stripe || saveStripePaymentMethodLoading}
            className="w-full bg-primary text-white py-2 px-4 rounded disabled:opacity-50 mt-4"
          >
            {saveStripePaymentMethodLoading
              ? t("payment_methods.adding")
              : t("payment_methods.add")}
          </button>
        </form>
      ) : (
        <form onSubmit={handleSubmit}>
          {!selectedPaymentMethod && (
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                {t("payment_methods.card_details")}
              </label>
              <div className="border rounded-md p-3 bg-white">
                <CardElement options={{ hidePostalCode: true }} />
              </div>
            </div>
          )}
          <button
            type="submit"
            disabled={isProcessing || isProcessingNewCard || !cardEntered}
            className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded disabled:opacity-50"
          >
            {isProcessing || isProcessingNewCard
              ? t("payment.processing")
              : <>
                  {t("payment.pay")} <Currency value={paymentAmount} />
                </>
            }
          </button>
        </form>
      )}

      {showSurchargeConfirmationModal && (
        <SurchargeConfirmationModal
          onConfirm={()=>proceedToPayment()}
          onClose={handleCancelSurchargeModal}
        />
      )}
    </div>
  );
};

export default StripePaymentForm;